import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { getCurrentQuickBooksRealmId, getSelectedBusinessData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { getUserRole } from "selectors/user";
import { useDispatch, useSelector } from "react-redux";
import BusinessesActions from "actions/BusinessesActions";
import Button from "nlib/ui/Button";
import DataStatuses from "const/DataStatuses";
import DocumentRecogniseStatuses from "const/DocumentRecogniseStatuses";
import Files from "utils/Files";
import MainApiActions from "actions/MainApiActions";
import Platforms from "utils/Platforms";
import React, { useCallback, useState } from "react";
import UiActions from "actions/UiActions";
import UiRoutes from "const/UiRoutes";
import UserRoles from "const/UserRoles";
import classNames from "classnames";

const AttachedDocumentActions = ({ disabled, compact, className, transaction, onEdit }) => {
  const {
    id: transactionId,
    status,
    documentAttachment,
    documentId,
    documentRecogniseStatus
  } = transaction;

  const dispatch = useDispatch();

  const { uiTexts, messages } = useSelector(getTextsData);

  const userRole = useSelector(getUserRole);

  const selectedBusinessData = useSelector(getSelectedBusinessData);

  const quickBooksBusiness = !!useSelector(getCurrentQuickBooksRealmId);

  const { id: selectedBusinessId, settings: { advancedDocumentsWorkflow } = {} } = selectedBusinessData;

  const [loading, setLoading] = useState(false);

  const businessUser = UserRoles.checkIsBusiness(userRole);

  const notEdible = Platforms.checkIsTouchDevice()
    || status === DataStatuses.TO_REPORT
    || status === DataStatuses.EXCLUDED
    || (status === DataStatuses.EXPORTED && !quickBooksBusiness)
    || (documentRecogniseStatus !== DocumentRecogniseStatuses.DONE);

  const canDetachDocument = (!businessUser || status === DataStatuses.NEED_REACTION)
    && status !== DataStatuses.TO_REPORT
    && status !== DataStatuses.EXCLUDED
    && (status !== DataStatuses.EXPORTED || quickBooksBusiness);

  const recogniseError = documentRecogniseStatus === DocumentRecogniseStatuses.ERROR;

  const handleDownloadClick = useCallback(async() => {
    if (documentAttachment) {
      setLoading(true);

      const link = await dispatch(MainApiActions.fetchAttachmentUrl(documentAttachment, true));

      if (link) Files.downloadContent(link);
      setLoading(false);
    }
  }, [dispatch, documentAttachment]);

  const handleUnpairClick = useCallback(async() => {
    const result = await dispatch(UiActions.showModal(messages.documentDetachConfirm, null, true));

    if (!result) return;
    setLoading(true);
    await dispatch(BusinessesActions.unpairMatches(transactionId, documentId));
    setLoading(false);
  }, [dispatch, documentId, messages, transactionId]);

  const handleEditClick = useCallback(() => {
    if (onEdit) {
      onEdit();
    } else {
      window.open(`/${selectedBusinessId}${UiRoutes.DOCUMENTS}?editItem=${documentId}`, "_blank");
    }
  }, [onEdit, selectedBusinessId, documentId]);

  return (
    <div className={classNames(Css.attachedDocumentActions, compact && Css.compact, className)}>
      {advancedDocumentsWorkflow && !recogniseError
        ? (
          <Button
            large outline
            className={classNames(Css.button, !notEdible && Css.edit)}
            title={notEdible ? uiTexts.open : uiTexts.edit}
            disabled={disabled || loading}
            icon={Icons.PencilSimpleLine}
            onClick={handleEditClick}>
            {!compact && (notEdible ? uiTexts.open : uiTexts.edit)}
          </Button>
        )
        : (
          !Platforms.checkIsTouchDevice() && documentAttachment?.key && (
            <Button
              large outline
              className={Css.button}
              title={uiTexts.download}
              disabled={disabled || loading}
              icon={Icons.Download}
              onClick={handleDownloadClick}>
              {!compact && uiTexts.download}
            </Button>
          )
        )}
      {canDetachDocument && (
        <Button
          large
          outline={!compact}
          className={Css.button}
          title={uiTexts.detach}
          danger={compact}
          disabled={disabled || loading}
          icon={Icons.X}
          onClick={handleUnpairClick}>
          {!compact && uiTexts.detach}
        </Button>
      )}
    </div>
  );
};

export default React.memo(AttachedDocumentActions);
